/**
 * Angular Spinner Component
 *
 * Migrated from the AngularJS `spinner` attribute directive.
 * Original: apps/b2b-ui/common/directives/spinner/spinner.directive.ts
 *
 * A purely presentational loading spinner: a 3D card that flips on its Y axis
 * every 400ms, cycling the letters of "ACME" through the brand colours.
 *
 * The original drove this imperatively with jQuery + setInterval. Here it is
 * fully data-bound: a setInterval (inside Angular's zone) mutates plain
 * component fields and template bindings repaint via change detection. This
 * avoids ElementRef/ViewChild/constructor-DI, which do not resolve reliably for
 * downgraded components in this JIT build.
 *
 * Usage in Angular templates:
 *   <app-spinner [spinnerHide]="isLoaded"></app-spinner>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-spinner spinner-hide="$ctrl.isLoaded"></ng-spinner>
 */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';

@Component({
  selector: 'app-spinner',
  standalone: true,
  template: require('./spinner.component.html'),
  styles: [require('./spinner.component.less')],
})
export class SpinnerComponent implements OnInit, OnDestroy {
  /** When true, fades the spinner out (opacity 0). Display-only flag. */
  @Input() spinnerHide = false;

  private static readonly WORD = 'ACME';
  private static readonly COLORS = ['#439899', '#f0533f', '#d9aa3d', '#439899'];

  private readonly letters = SpinnerComponent.WORD.split('');

  // Template-bound animation state.
  frontBg = SpinnerComponent.COLORS[0];
  backBg = SpinnerComponent.COLORS[0];
  frontLetter = this.letters[0];
  backLetter = this.letters[0];
  flipperTransform = 'rotateY(0deg)';
  hover = false;

  private count = 0;
  private lCount = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.intervalId = setInterval(() => this.tick(), 400);
  }

  ngOnDestroy(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private tick(): void {
    const colors = SpinnerComponent.COLORS;
    const deg = ++this.count * 180;
    const flippedSide = this.count % 2;
    const flippedLetter = this.lCount % 2;

    // Recolour the side rotating into view.
    if (flippedSide) {
      this.frontBg = colors[this.count % 4];
    } else {
      this.backBg = colors[this.count % 4];
    }

    // Advance the letter on the alternating face.
    const next = this.letters[++this.lCount % this.letters.length];
    if (flippedLetter) {
      this.frontLetter = next;
    } else {
      this.backLetter = next;
    }

    this.hover = !this.hover;
    this.flipperTransform = `rotateY(${deg}deg)`;
  }
}
